import { FaFlask, FaXRay, FaHeartbeat, FaSyringe, FaMicroscope, FaVial } from "react-icons/fa";
import { MdLocalHospital, MdScience, MdHealthAndSafety } from "react-icons/md";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Card from "react-bootstrap/Card";
import Badge from "react-bootstrap/Badge";
import Table from "react-bootstrap/Table";

type Procedure = {
	name: string;
	total: number;
};

type Props = {
	totalProceduresByProcedure: Procedure[];
};

// Função para determinar o ícone baseado no nome do procedimento
const getProcedureIcon = (procedureName: string) => {
	const name = procedureName.toLowerCase();

	if (name.includes("glicemia") || name.includes("glicose")) {
		return <FaVial className="text-warning" />;
	}
	if (name.includes("hemograma") || name.includes("coleta")) {
		return <FaMicroscope className="text-info" />;
	}
	if (
		name.includes("dosagem") ||
		name.includes("creatinina") ||
		name.includes("ureia") ||
		name.includes("potassio") ||
		name.includes("sodio") ||
		name.includes("troponina") ||
		name.includes("cpk") ||
		name.includes("transaminase") ||
		name.includes("amilase") ||
		name.includes("lipase") ||
		name.includes("bilirrubina") ||
		name.includes("gama") ||
		name.includes("fosfatase") ||
		name.includes("magnesio") ||
		name.includes("calcio") ||
		name.includes("proteinas") ||
		name.includes("hcg") ||
		name.includes("microalbumina")
	) {
		return <FaFlask className="text-primary" />;
	}
	if (name.includes("radiografia") || name.includes("raio-x")) {
		return <FaXRay className="text-success" />;
	}
	if (name.includes("ultrassonografia") || name.includes("ultrassom")) {
		return <MdLocalHospital className="text-info" />;
	}
	if (name.includes("eletrocardiograma") || name.includes("ecg")) {
		return <FaHeartbeat className="text-danger" />;
	}
	if (
		name.includes("teste") ||
		name.includes("pesquisa") ||
		name.includes("detecção") ||
		name.includes("determinação") ||
		name.includes("gasometria")
	) {
		return <FaSyringe className="text-warning" />;
	}

	return <MdScience className="text-muted" />;
};

// Função para determinar a categoria do procedimento
const getProcedureCategory = (procedureName: string) => {
	const name = procedureName.toLowerCase();

	if (name.includes("radiografia") || name.includes("raio-x")) {
		return "Imagem";
	}
	if (name.includes("ultrassonografia") || name.includes("ultrassom")) {
		return "Imagem";
	}
	if (name.includes("eletrocardiograma") || name.includes("ecg")) {
		return "Cardiologia";
	}
	if (name.includes("teste") || name.includes("pesquisa") || name.includes("detecção")) {
		return "Teste";
	}
	if (name.includes("gasometria")) {
		return "Gasometria";
	}

	return "Laboratorial";
};

// Função para determinar a cor da categoria
const getCategoryColor = (category: string) => {
	switch (category) {
		case "Imagem":
			return "success";
		case "Cardiologia":
			return "danger";
		case "Teste":
			return "warning";
		case "Gasometria":
			return "info";
		default:
			return "primary";
	}
};

export function ProceduresList({ totalProceduresByProcedure }: Props) {
	// Agrupar procedimentos por categoria
	const groupedProcedures = totalProceduresByProcedure.reduce((acc, procedure) => {
		const category = getProcedureCategory(procedure.name);
		if (!acc[category]) {
			acc[category] = [];
		}
		acc[category].push(procedure);
		return acc;
	}, {} as Record<string, Procedure[]>);

	// Ordenar categorias por total de procedimentos
	const sortedCategories = Object.entries(groupedProcedures).sort(([, a], [, b]) => {
		const totalA = a.reduce((sum, p) => sum + p.total, 0);
		const totalB = b.reduce((sum, p) => sum + p.total, 0);
		return totalB - totalA;
	});

	// Calcular totais
	const totalProcedures = totalProceduresByProcedure.reduce((sum, p) => sum + p.total, 0);
	const topProcedures = totalProceduresByProcedure.sort((a, b) => b.total - a.total).slice(0, 5);

	return (
		<Row className="p-3">
			{/* Card Principal - Resumo Geral */}
			<Col sm={12} lg={4} className="mb-3">
				<Card className="h-100 shadow-sm border-0">
					<Card.Header className="bg-gradient-primary text-white border-0">
						<div className="d-flex align-items-center justify-content-center">
							<div className="bg-white bg-opacity-20 rounded-circle p-2 me-3">
								<MdHealthAndSafety size={24} />
							</div>
							<div>
								<h5 className="mb-1 fw-bold">Procedimentos Realizados</h5>
								<small className="opacity-75">Visão Geral</small>
							</div>
						</div>
					</Card.Header>

					<Card.Body className="p-4">
						<div className="text-center mb-4">
							<h2 className="fw-bold text-primary mb-2">
								{totalProcedures.toLocaleString()}
							</h2>
							<p className="text-muted mb-0">Total de Procedimentos</p>
						</div>

						{/* Top 5 Procedimentos */}
						<div className="mb-3">
							<h6 className="fw-bold text-dark mb-3">Top 5 Procedimentos</h6>
							{topProcedures.map((procedure, index) => (
								<div
									key={index}
									className="d-flex align-items-center justify-content-between mb-2 p-2 bg-light rounded"
								>
									<div className="d-flex align-items-center">
										<div className="me-2">
											{getProcedureIcon(procedure.name)}
										</div>
										<div
											className="text-truncate"
											style={{ maxWidth: "200px" }}
										>
											<small className="fw-bold text-dark d-block">
												{procedure.name}
											</small>
											<small className="text-muted">
												{procedure.total.toLocaleString()}
											</small>
										</div>
									</div>
									<Badge bg="primary" className="fs-6">
										{((procedure.total / totalProcedures) * 100).toFixed(1)}%
									</Badge>
								</div>
							))}
						</div>

						{/* Categorias */}
						<div>
							<h6 className="fw-bold text-dark mb-3">Por Categoria</h6>
							{sortedCategories.map(([category, procedures]) => {
								const categoryTotal = procedures.reduce(
									(sum, p) => sum + p.total,
									0
								);
								return (
									<div
										key={category}
										className="d-flex align-items-center justify-content-between mb-2"
									>
										<div className="d-flex align-items-center">
											<Badge bg={getCategoryColor(category)} className="me-2">
												{category}
											</Badge>
											<small className="text-muted">
												{procedures.length} tipos
											</small>
										</div>
										<small className="fw-bold text-dark">
											{categoryTotal.toLocaleString()}
										</small>
									</div>
								);
							})}
						</div>
					</Card.Body>
				</Card>
			</Col>

			{/* Card - Lista Detalhada */}
			<Col sm={12} lg={8} className="mb-3">
				<Card className="h-100 shadow-sm border-0">
					<Card.Header className="bg-gradient-info text-white border-0">
						<div className="d-flex align-items-center justify-content-between">
							<div className="d-flex align-items-center">
								<div className="bg-white bg-opacity-20 rounded-circle p-2 me-3">
									<MdScience size={24} />
								</div>
								<div>
									<h5 className="mb-1 fw-bold">
										Lista Completa de Procedimentos
									</h5>
									<small className="opacity-75">Detalhamento por Tipo</small>
								</div>
							</div>
							<Badge bg="light" text="dark" className="fs-6 px-3 py-2">
								{totalProceduresByProcedure.length} tipos
							</Badge>
						</div>
					</Card.Header>

					<Card.Body className="p-0">
						<div className="overflow-y-scroll" style={{ maxHeight: "600px" }}>
							<Table responsive hover className="mb-0">
								<thead className="bg-light sticky-top">
									<tr>
										<th className="ps-3">Procedimento</th>
										<th className="text-center">Categoria</th>
										<th className="text-end pe-3">Quantidade</th>
									</tr>
								</thead>
								<tbody>
									{totalProceduresByProcedure
										.sort((a, b) => b.total - a.total)
										.map((procedure, index) => {
											const category = getProcedureCategory(procedure.name);
											const percentage = (
												(procedure.total / totalProcedures) *
												100
											).toFixed(1);

											return (
												<tr key={index} className="align-middle">
													<td className="ps-3">
														<div className="d-flex align-items-center">
															<div className="me-3">
																{getProcedureIcon(procedure.name)}
															</div>
															<div>
																<div
																	className="fw-bold text-dark"
																	style={{ fontSize: "0.9rem" }}
																>
																	{procedure.name}
																</div>
																<small className="text-muted">
																	{percentage}% do total
																</small>
															</div>
														</div>
													</td>
													<td className="text-center">
														<Badge bg={getCategoryColor(category)}>
															{category}
														</Badge>
													</td>
													<td className="text-end pe-3">
														<div className="fw-bold text-dark">
															{procedure.total.toLocaleString()}
														</div>
													</td>
												</tr>
											);
										})}
								</tbody>
							</Table>
						</div>
					</Card.Body>
				</Card>
			</Col>
		</Row>
	);
}
